import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AskOurDataService } from '@app/core/services/ask-our-data/ask-our-data.service';
import { AlertService } from '@app/core/services/alert/alert.service';


@Component({
  selector: 'app-response-feedback',
  templateUrl: './response-feedback.component.html',
  styleUrl: './response-feedback.component.css'
})
export class ResponseFeedbackComponent implements OnChanges {
  @Input() groupId: number = 0;
  @Input() prompt: string = '';
  @Input() dbId: string = '';
  @Input() sessionId: string = '';
  @Input() toolUsed: string | null = null;
  @Input() chatIdBackend: string | null = null;
  @Input() isUseful: boolean | null = null;
  @Input() responseData: string | null = null;
  
  @Output() closeFeedbackFormEvent = new EventEmitter<number>();
  @Output() openFeedbackFormEvent = new EventEmitter<{ groupId: number, feedbackType: string | null }>();
  
  showFeedbackForm: boolean = false;
  feedbackType: 'thumbsUp' | 'thumbsDown' | null = null;
  selectedRating: number | null = null;
  feedbackSuggestions: string = '';
  feedbackSubmitted: boolean = false;
  showSuccessMessage: boolean = false;
  selectedFeedbackSubjects: string[] = [];
  otherFeedbackText: string = '';
  showOtherTextInput: boolean = false;
  showTableIssuesChildren: boolean = false;
  
  tableIssuesOption = {
    label: 'Table issues: (select all that apply)',
    value: 'tableIssues',
    childOptions: [
      { label: 'Column names', value: 'columnNames' },
      { label: 'Data displayed', value: 'dataDisplayed' }
    ]
  };
  
  feedbackSubjectOptions = [
    { label: 'Summary not relevant', value: 'summaryNotRelevant' },
    { label: 'Visualization', value: 'visualization' },
    { label: 'Other - I will explain below', value: 'other' }
  ];

  constructor(
    private askOurDataService: AskOurDataService,
    private alertService: AlertService
  ) {}
  
  ngOnChanges(): void {
    this.initializeFeedbackState();
  }
  
  
  initializeFeedbackState(): void {
    if (this.isUseful !== null) {
      this.feedbackType = this.isUseful ? 'thumbsUp' : 'thumbsDown';
      this.feedbackSubmitted = true;
      this.showFeedbackForm = false;
    }
    else {
      this.resetFeedbackState();
    }
  }
  
  resetFeedbackState(): void {
    if (this.isUseful !== null) {
      this.initializeFeedbackState();
    } else {
      this.feedbackSubmitted = false;
      this.feedbackType = null;
      this.selectedRating = null;
      this.feedbackSuggestions = '';
      this.showFeedbackForm = false;
      this.selectedFeedbackSubjects = [];
      this.otherFeedbackText = '';
      this.showOtherTextInput = false;
      this.showTableIssuesChildren = false;
    }
  }

  provideFeedback(feedbackType: 'thumbsUp' | 'thumbsDown'): void {
    // If feedback has already been submitted, don't allow changes or opening the form
    if (this.feedbackSubmitted) {
      return;
    }
    
    // If clicked on same button while form is not showing, show the form
    if (!this.showFeedbackForm || this.feedbackType !== feedbackType) {
      this.showFeedbackForm = true;
      this.openFeedbackFormEvent.emit({ groupId: this.groupId, feedbackType: feedbackType });
      
      // If switching between feedback types, reset the data
      if (this.showFeedbackForm && this.feedbackType !== feedbackType) {
        this.selectedRating = null;
        this.feedbackSuggestions = '';
        
        // Reset feedback subjects when switching types
        this.selectedFeedbackSubjects = [];
        this.otherFeedbackText = '';
        this.showOtherTextInput = false;
        this.showTableIssuesChildren = false;
      }
      
      this.feedbackType = feedbackType;
    } else {
      this.openFeedbackFormEvent.emit({ groupId: this.groupId, feedbackType: null });
      this.closeFeedbackForm();
    }
  }

  closeFeedbackForm(): void {
    this.showFeedbackForm = false;
    if (!this.feedbackSubmitted) {
      this.feedbackType = null;
      this.openFeedbackFormEvent.emit({ groupId: this.groupId, feedbackType: null });
    }
    this.closeFeedbackFormEvent.emit(this.groupId);
  }

  selectRating(rating: number): void {
    this.selectedRating = rating;
  }

  isFormValid(): boolean {
    // Require rating for both feedback types
    if (this.selectedRating === null) return false;
    
    // For thumbs down, require at least one feedback subject to be selected
    if (this.feedbackType === 'thumbsDown' && this.selectedFeedbackSubjects.length === 0) {
      return false;
    }
    
    // For thumbs down with 'other' selected, require other text
    if (this.feedbackType === 'thumbsDown' && 
        this.isSubjectSelected('other') && 
        !this.otherFeedbackText.trim()) {
      return false;
    }
    
    // For thumbs down with 'tableIssues' selected, require at least one child checkbox
    if (this.feedbackType === 'thumbsDown' && 
        this.isSubjectSelected('tableIssues') && 
        !this.isSubjectSelected('columnNames') && 
        !this.isSubjectSelected('dataDisplayed')) {
      return false;
    }
    
    return true;
  }
  
  // Handle checkbox selections
  onTableIssuesChange(event: any): void {
    if (event.checked) {
      // Add only the parent
      this.selectedFeedbackSubjects.push('tableIssues');
      
      // Show child checkboxes without selecting them
      this.showTableIssuesChildren = true;
    } else {
      // Remove parent and all children
      this.selectedFeedbackSubjects = this.selectedFeedbackSubjects.filter(
        subject => subject !== 'tableIssues' && 
                   subject !== 'columnNames' && 
                   subject !== 'dataDisplayed'
      );
      
      // Hide child checkboxes when parent is unchecked
      this.showTableIssuesChildren = false;
    }
  }

  onChildOptionChange(event: any, value: string): void {
    if (event.checked) {
      // Add child option
      this.selectedFeedbackSubjects.push(value);
      
      // If any child is checked, also check the parent
      if (!this.selectedFeedbackSubjects.includes('tableIssues')) {
        this.selectedFeedbackSubjects.push('tableIssues');
      }
    } else {
      // Remove child option only, keep parent selected
      this.selectedFeedbackSubjects = this.selectedFeedbackSubjects.filter(subject => subject !== value);
    }
  }

  hasSelectedTableChildren(): boolean {
    return this.selectedFeedbackSubjects.includes('columnNames') || 
           this.selectedFeedbackSubjects.includes('dataDisplayed');
  }

  onFeedbackSubjectChange(event: any, value: string): void {
    if (event.checked) {
      this.selectedFeedbackSubjects.push(value);
      
      // Show text input if 'other' is selected
      if (value === 'other') {
        this.showOtherTextInput = true;
      }
    } else {
      this.selectedFeedbackSubjects = this.selectedFeedbackSubjects.filter(subject => subject !== value);
      
      // Hide text input if 'other' is unselected
      if (value === 'other') {
        this.showOtherTextInput = false;
        this.otherFeedbackText = '';
      }
    }
  }

  isSubjectSelected(value: string): boolean {
    return this.selectedFeedbackSubjects.includes(value);
  }
  
  hasFeedbackBeenSubmitted(): boolean {
    return this.feedbackSubmitted;
  }

  closeSuccessMessage() {
    this.showSuccessMessage = false;
  }

  submitFeedback(): void {
    let feedbackData: any = {
      database_id: parseInt(this.dbId, 10),
      session_identifier: this.sessionId,
      chat_id: this.chatIdBackend,
      user_query: this.prompt,
      tool_used: this.toolUsed,
      quality_score: this.selectedRating,
      additional_comments: this.feedbackSuggestions || null,
      response_data: this.responseData || null
    };
    
    if (this.feedbackType === 'thumbsUp') {
      feedbackData.is_useful = true;
      feedbackData.table_issues = null;
      feedbackData.has_summary_issue = null;
      feedbackData.has_visualization_issue = null;
      feedbackData.has_other_issue = null;
      feedbackData.other_issue_details = null;
    } else if (this.feedbackType === 'thumbsDown') {
      feedbackData.is_useful = false;
      feedbackData.table_issues = {
        has_table_issues: this.isSubjectSelected('tableIssues'),
        column_names: this.isSubjectSelected('columnNames'),
        data_displayed: this.isSubjectSelected('dataDisplayed')
      };
      feedbackData.has_summary_issue = this.isSubjectSelected('summaryNotRelevant');
      feedbackData.has_visualization_issue = this.isSubjectSelected('visualization');
      feedbackData.has_other_issue = this.isSubjectSelected('other');
      
      if (this.isSubjectSelected('other')) {
        feedbackData.other_issue_details = this.otherFeedbackText;
      } else {
        feedbackData.other_issue_details = null;
      }
    }

    this.askOurDataService.saveFeedback(feedbackData).subscribe({
      next: () => {
        this.feedbackSubmitted = true;
        this.isUseful = this.feedbackType === 'thumbsUp';
        this.showFeedbackForm = false;
        this.openFeedbackFormEvent.emit({ groupId: this.groupId, feedbackType: '' });
        this.showSuccessMessage = true;
      },
      error: () => {
        this.alertService.showError('Error submitting feedback. Please try again later.');
      }
    });
  }
}